// Diario de actividad del TPV — API pública (singleton).
//
// Uso en los puntos de captura (una línea):
//   journal.track("drawer.click");
//   journal.track("comandar.click", { lines, total }, { mesaId, sessionId });
//
// Siempre activo en las pantallas TPV del personal (rutas `tpv.*`); nunca
// en la carta QR ni en páginas públicas. Cada evento lleva el `actionId`
// del click en curso (Fase 5) para unirlo con lo que hizo el backend.
//
// Nota de dependencias: este módulo lo importa el middleware que registra
// `redux/store`, así que NO importa store ni `utils/axios` de forma
// estática (ambos importan el store → ciclo). axios se carga al enviar.

import { getCurrentAction, getScreen } from "src/utils/debug/debugTrace";
import { createJournalClient, ENDPOINT } from "./journalClient";
import { isJournalPath } from "./journalCore";

let getState = null;

/** Lo llama el middleware al crearse el store: contexto de mesa/sesión actual. */
export function setStateGetter(fn) {
  getState = typeof fn === "function" ? fn : null;
}

function currentContext() {
  const ctx = { actionId: getCurrentAction()?.id || null, screen: getScreen() };
  try {
    const s = getState ? getState() : null;
    const selected = s?.mesas?.selectedId;
    if (selected != null && selected !== -1) ctx.mesaId = selected;
    const mesa = s?.mesas?.mesa;
    const sessionId = mesa && (mesa.id === selected || selected == null || selected === -1)
      ? mesa.sessionId ?? mesa.idSession ?? null
      : null;
    if (sessionId) ctx.sessionId = sessionId;
  } catch { /* noop */ }
  return ctx;
}

function loadAxios() {
  // eslint-disable-next-line global-require
  return require("src/utils/axios").default;
}

async function send(events) {
  const axios = loadAxios();
  try {
    return await axios.post(ENDPOINT, { events });
  } catch (err) {
    const status = err?.status ?? err?.response?.status ?? null;
    const e = new Error("journal send failed");
    e.status = status;
    throw e;
  }
}

function sendKeepalive(events) {
  try {
    if (typeof fetch !== "function") return false;
    const axios = loadAxios();
    const common = axios?.defaults?.headers?.common || {};
    const headers = { "Content-Type": "application/json" };
    if (common.Authorization) headers.Authorization = common.Authorization;
    if (common.SessionIdentifier) headers.SessionIdentifier = common.SessionIdentifier;
    const emp = window.localStorage.getItem("EmployeeSessionToken");
    if (emp) headers["X-Employee-Session"] = emp;
    const base = String(axios?.defaults?.baseURL || "").replace(/\/+$/, "");
    fetch(`${base}/${ENDPOINT}`, {
      method: "POST",
      keepalive: true,
      headers,
      body: JSON.stringify({ events }),
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

const client = createJournalClient({
  storage: typeof window !== "undefined" ? window.localStorage : null,
  send,
  sendKeepalive,
  now: () => Date.now(),
  setTimer: (fn, ms) => setTimeout(fn, ms),
  clearTimer: (id) => clearTimeout(id),
  isActive: () => typeof window !== "undefined" && isJournalPath(window.location.pathname),
  context: currentContext,
});

let hideInstalled = false;
function installHideFlush() {
  if (hideInstalled || typeof window === "undefined") return;
  hideInstalled = true;
  window.addEventListener("pagehide", () => client.flushOnHide());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") client.flushOnHide();
  });
}

// ── Cobro: abrir / confirmar / cerrar sin cobrar ─────────────────────
let payOpen = false;
let payConfirmed = false;

export const journal = {
  track(type, data, extra) {
    installHideFlush();
    return client.track(type, data, extra);
  },
  /** El diálogo de cobro se abre. */
  payOpened(extra) {
    payOpen = true;
    payConfirmed = false;
    return journal.track("pay.open", undefined, extra);
  },
  /** El camarero pulsa Cobrar. */
  payConfirm(data, extra) {
    payConfirmed = true;
    return journal.track("pay.confirm", data, extra);
  },
  /** El diálogo se cierra: si no se llegó a cobrar, `pay.cancel`. */
  payClosed(extra) {
    if (!payOpen) return null;
    payOpen = false;
    const confirmed = payConfirmed;
    payConfirmed = false;
    return confirmed ? null : journal.track("pay.cancel", undefined, extra);
  },
  flush: () => client.flush(),
};

export default journal;
